import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Typography, Button, TextField, Alert, CircularProgress,
  IconButton, Card, CardContent, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, InputAdornment,
  FormControl, InputLabel, Select, MenuItem, Avatar, Divider,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack as BackIcon, Search as SearchIcon, Add as AddIcon,
  Edit as EditIcon, CloudUpload as UploadIcon, ShoppingCart as CartIcon,
  CheckCircle as CheckIcon, Image as ImageIcon, Category as CategoryIcon,
  Refresh as RefreshIcon, Update as UpdateIcon,
} from '@mui/icons-material';
import authInstance from '../../services/httpClient';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CatalogProduct {
  ID: string;
  SKU: string;
  Name: string;
  ShortDescription: string;
  Description: string;
  Category: string;
  PriceTier1: number;
  PriceTier3: number;
  PriceTiers?: Record<string, number>;
  Attachments?: Array<{ IsDefault: boolean; DownloadUrl: string }>;
}

interface UberCategory {
  id: string;
  title: string;
  subtitle?: string;
  itemCount: number;
  isActive: boolean;
}

interface UberMatchInfo {
  found: boolean;
  itemId?: string;
  name?: string;
  priceCents?: number;
  imageUrl?: string | null;
  categories?: string[];
  categoryNames?: string[];
  differences?: string[];
}

interface SearchResult {
  product: CatalogProduct;
  uberMatch: UberMatchInfo | null;
}

interface Props {
  onBack: () => void;
  initialSearch?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TEAL = '#00897b';
const TEAL_DARK = '#00695c';
const TEAL_LIGHT = '#e0f2f1';
const STEPS = ['Search', 'Edit', 'Categories', 'Upload'];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount);

/** Tier 1 price price (the base retail price) */
const getProductPrice = (p: CatalogProduct): number =>
  p.PriceTier1 ?? p.PriceTier3 ?? 0;

/** Uber price = Tier1 / divisor (accounts for Uber's commission) */
const calcUberPrice = (tier1: number, divisor = 0.8275): number =>
  Math.round((tier1 / divisor) * 100) / 100;

const getProductDescription = (p: CatalogProduct): string =>
  p.ShortDescription || p.Description || `1x ${p.Name}`;

const getProductImage = (p: CatalogProduct): string =>
  p.Attachments?.find((a) => a.IsDefault)?.DownloadUrl ||
  p.Attachments?.[0]?.DownloadUrl ||
  '';

// ---------------------------------------------------------------------------
// Custom Stepper
// ---------------------------------------------------------------------------

const WizardStepper: React.FC<{ activeStep: number }> = ({ activeStep }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      mb: 4,
      px: 2,
    }}
  >
    {STEPS.map((label, idx) => {
      const isCompleted = idx < activeStep;
      const isActive = idx === activeStep;
      const isFuture = idx > activeStep;

      return (
        <React.Fragment key={label}>
          {/* Circle */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 72 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isCompleted ? '#43a047' : isActive ? TEAL : '#e0e0e0',
                color: '#fff',
                fontWeight: 700,
                fontSize: 15,
                transition: 'background-color 0.3s ease',
                boxShadow: isActive ? `0 0 0 4px ${TEAL_LIGHT}` : 'none',
              }}
            >
              {isCompleted ? <CheckIcon sx={{ fontSize: 22 }} /> : idx + 1}
            </Box>
            <Typography
              variant="caption"
              sx={{
                mt: 0.75,
                fontWeight: isActive ? 700 : 500,
                color: isActive ? TEAL : isFuture ? 'text.disabled' : 'text.secondary',
                transition: 'color 0.3s ease',
              }}
            >
              {label}
            </Typography>
          </Box>

          {/* Connecting line */}
          {idx < STEPS.length - 1 && (
            <Box
              sx={{
                flex: 1,
                height: 3,
                mx: 1,
                mt: -2,
                borderRadius: 2,
                bgcolor: idx < activeStep ? '#43a047' : '#e0e0e0',
                transition: 'background-color 0.3s ease',
              }}
            />
          )}
        </React.Fragment>
      );
    })}
  </Box>
);

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const AddProductToUber: React.FC<Props> = ({ onBack, initialSearch }) => {
  // Wizard state
  const [step, setStep] = useState(0);

  // Shared fields (sticky across steps)
  const [storeId, setStoreId] = useState('');

  // Step 1 — Search
  const [searchTerm, setSearchTerm] = useState(initialSearch || '');
  const [searchType, setSearchType] = useState<'auto' | 'name' | 'sku'>('auto');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<CatalogProduct | null>(null);

  // Update mode (when product already exists on Uber)
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [selectedUberMatch, setSelectedUberMatch] = useState<UberMatchInfo | null>(null);

  // Step 2 — Edit overrides
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editPrice, setEditPrice] = useState<number | ''>('');
  const [editImageUrl, setEditImageUrl] = useState('');
  const [descriptionMode, setDescriptionMode] = useState<'short' | 'full'>('short');

  // Step 3 — Categories
  const [categories, setCategories] = useState<UberCategory[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');

  // Step 4 — Upload
  const [uploading, setUploading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Uber markup divisor (fetched from system settings)
  const [markupDivisor, setMarkupDivisor] = useState(0.8275);

  useEffect(() => {
    authInstance.get('/admin/tools/uber-markup')
      .then((res) => { if (res.data.success) setMarkupDivisor(res.data.data.divisor); })
      .catch(() => {}); // fallback to default
  }, []);

  // ---------------------------------------------------------------------------
  // Step navigation helpers
  // ---------------------------------------------------------------------------

  const goNext = () => {
    setError(null);
    setStep((s) => Math.min(s + 1, 3));
  };

  const goBack = () => {
    setError(null);
    setStep((s) => Math.max(s - 1, 0));
  };

  // ---------------------------------------------------------------------------
  // Step 1 — Search
  // ---------------------------------------------------------------------------

  const handleSearch = useCallback(async () => {
    if (!searchTerm.trim()) {
      setError('Please enter a search term.');
      return;
    }

    setSearching(true);
    setError(null);
    setSearchResults([]);

    try {
      const response = await authInstance.post(
        '/admin/tools/uber-menu/search-products',
        { search: searchTerm.trim(), searchType, limit: 20 },
      );

      if (response.data.success) {
        setSearchResults(response.data.data || []);
        if ((response.data.data || []).length === 0) {
          setError('No products found. Try a different search term.');
        }
      } else {
        setError(response.data.error || 'Search failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Search failed');
    } finally {
      setSearching(false);
    }
  }, [searchTerm, searchType]);

  const handleSelectProduct = (product: CatalogProduct) => {
    setSelectedProduct(product);
    setIsUpdateMode(false);
    setSelectedUberMatch(null);
    setEditName(product.Name);
    setEditDescription(getProductDescription(product) || '');
    // Auto-calculate Uber price: Tier1 / divisor
    const tier1 = getProductPrice(product);
    setEditPrice(tier1 > 0 ? calcUberPrice(tier1, markupDivisor) : '');
    setEditImageUrl(getProductImage(product) || '');
    setSelectedCategoryIds([]);
    setUploadResult(null);
    goNext();
  };

  const handleSelectForUpdate = (result: SearchResult) => {
    setSelectedProduct(result.product);
    setSelectedUberMatch(result.uberMatch ?? null);
    setIsUpdateMode(true);
    // Pre-fill with catalog data (source of truth)
    setEditName(result.product.Name);
    setEditDescription(getProductDescription(result.product));
    const tier1 = getProductPrice(result.product);
    setEditPrice(tier1 > 0 ? calcUberPrice(tier1, markupDivisor) : '');
    setEditImageUrl(getProductImage(result.product));
    // Pre-select the Uber categories this item is already in
    if (result.uberMatch?.categories) {
      setSelectedCategoryIds(result.uberMatch.categories);
    }
    setUploadResult(null);
    goNext();
  };

  // ---------------------------------------------------------------------------
  // Step 2 — Price helpers
  // ---------------------------------------------------------------------------

  const applyUberFormula = () => {
    if (!selectedProduct) return;
    const tier1 = getProductPrice(selectedProduct);
    if (tier1 > 0) setEditPrice(calcUberPrice(tier1, markupDivisor));
  };

  // ---------------------------------------------------------------------------
  // Step 3 — Load categories
  // ---------------------------------------------------------------------------

  const loadCategories = useCallback(async () => {
    setLoadingCategories(true);
    setError(null);

    try {
      const response = await authInstance.post(
        '/admin/tools/uber-menu/categories',
        { storeId: storeId.trim() || undefined },
      );

      if (response.data.success) {
        setCategories(response.data.data?.categories || []);
      } else {
        setError(response.data.error || 'Failed to load categories');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load categories');
    } finally {
      setLoadingCategories(false);
    }
  }, [storeId]);

  // Auto-load categories when entering step 3 (or when storeId changes)
  const [prevStoreId, setPrevStoreId] = useState(storeId);
  useEffect(() => {
    if (step === 2) {
      if (categories.length === 0 || storeId !== prevStoreId) {
        setPrevStoreId(storeId);
        loadCategories();
      }
    }
  }, [step, storeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-search when initialSearch prop is provided (e.g. from Product Management page)
  useEffect(() => {
    if (initialSearch && searchTerm === initialSearch && searchResults.length === 0 && !searching) {
      handleSearch();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleCategory = (catId: string) => {
    setSelectedCategoryIds((prev) =>
      prev.includes(catId) ? prev.filter((id) => id !== catId) : [...prev, catId],
    );
  };

  const filteredCategories = categories.filter((c) =>
    c.title.toLowerCase().includes(categorySearch.toLowerCase()),
  );

  // ---------------------------------------------------------------------------
  // Step 4 — Upload
  // ---------------------------------------------------------------------------

  const handleUpload = async () => {
    setConfirmOpen(false);
    setUploading(true);
    setError(null);
    setUploadResult(null);

    const endpoint = isUpdateMode
      ? '/admin/tools/uber-menu/update-product'
      : '/admin/tools/uber-menu/add-product';

    try {
      const response = await authInstance.post(
        endpoint,
        {
          storeId: storeId.trim() || undefined,
          product: selectedProduct,
          categoryIds: selectedCategoryIds,
          overrides: {
            name: editName.trim() || undefined,
            description: editDescription.trim() || undefined,
            price: editPrice || undefined,
            imageUrl: editImageUrl.trim() || undefined,
          },
        },
      );

      if (response.data.success) {
        const defaultMsg = isUpdateMode ? 'Product updated successfully' : 'Product added successfully';
        setUploadResult(response.data.data || { message: defaultMsg });
      } else {
        setError(response.data.error || 'Upload failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Refresh Uber cache
  // ---------------------------------------------------------------------------

  const handleRefreshCache = async () => {
    try {
      await authInstance.post(
        '/admin/tools/uber-menu/invalidate-cache',
        { target: 'all' },
      );
      // Show brief success feedback
      setError(null);
    } catch (err: any) {
      setError('Failed to refresh cache');
    }
  };

  // ---------------------------------------------------------------------------
  // Image fallback
  // ---------------------------------------------------------------------------

  const ProductAvatar: React.FC<{ url?: string; name: string; size: number }> = ({
    url,
    name,
    size,
  }) => {
    const [imgError, setImgError] = useState(false);

    if (url && !imgError) {
      return (
        <Avatar
          variant="rounded"
          src={url}
          onError={() => setImgError(true)}
          sx={{ width: size, height: size, borderRadius: 2 }}
        />
      );
    }

    return (
      <Avatar
        variant="rounded"
        sx={{
          width: size,
          height: size,
          borderRadius: 2,
          bgcolor: '#e0e0e0',
          color: '#9e9e9e',
          fontSize: size * 0.4,
          fontWeight: 700,
        }}
      >
        {name?.charAt(0)?.toUpperCase() || '?'}
      </Avatar>
    );
  };

  // ---------------------------------------------------------------------------
  // Render — Step 1: Search
  // ---------------------------------------------------------------------------

  const renderStep1 = () => (
    <Box>
      {/* Store ID override (optional — defaults to SSM config) */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 3, overflow: 'visible' }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: TEAL }}>
              Uber Eats Connection
            </Typography>
            <Button
              variant="text"
              size="small"
              onClick={handleRefreshCache}
              startIcon={<RefreshIcon />}
              sx={{ color: TEAL, textTransform: 'none' }}
            >
              Refresh Cache
            </Button>
          </Box>
          <TextField
            label="Store ID Override (optional)"
            size="small"
            value={storeId}
            onChange={(e) => setStoreId(e.target.value)}
            placeholder="Leave blank to use default store"
            fullWidth
            helperText="Uber token and default store ID are auto-configured from AWS"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CartIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* Search bar */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 3, overflow: 'visible' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: TEAL }}>
            Search catalog products
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <TextField
              label="Search"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !searching) handleSearch();
              }}
              placeholder="Product name or SKU..."
              sx={{ flex: '1 1 260px' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Search By</InputLabel>
              <Select
                value={searchType}
                label="Search By"
                onChange={(e) => setSearchType(e.target.value as 'auto' | 'name' | 'sku')}
              >
                <MenuItem value="auto">Auto</MenuItem>
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="sku">SKU</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={searching || !searchTerm.trim()}
              startIcon={searching ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
              sx={{
                bgcolor: TEAL,
                '&:hover': { bgcolor: TEAL_DARK },
                height: 40,
                px: 3,
              }}
            >
              {searching ? 'Searching...' : 'Search'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Search results */}
      {searching && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: TEAL }} />
        </Box>
      )}

      {!searching && searchResults.length > 0 && (
        <Box>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
            {searchResults.length} product{searchResults.length !== 1 ? 's' : ''} found
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {searchResults.map((result) => {
              const product = result.product;
              const uberMatch = result.uberMatch;
              const isOnUber = uberMatch?.found === true;
              const hasDifferences = isOnUber && uberMatch?.differences && uberMatch.differences.length > 0;
              const isUpToDate = isOnUber && !hasDifferences;

              return (
                <Card
                  key={product.ID}
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                    '&:hover': { boxShadow: 4, borderColor: TEAL },
                    ...(isOnUber && { borderLeft: '4px solid #43a047' }),
                  }}
                >
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                      <ProductAvatar url={getProductImage(product)} name={product.Name} size={60} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                          {product.Name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                          <Chip
                            label={product.SKU}
                            size="small"
                            variant="outlined"
                            sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                          />
                          {product.Category && (
                            <Chip
                              icon={<CategoryIcon sx={{ fontSize: '14px !important' }} />}
                              label={product.Category}
                              size="small"
                              variant="outlined"
                              color="default"
                            />
                          )}
                          {/* Uber status chips */}
                          {isOnUber && isUpToDate && (
                            <Chip
                              icon={<CheckIcon sx={{ fontSize: '14px !important', color: '#fff !important' }} />}
                              label="Up to date"
                              size="small"
                              sx={{
                                bgcolor: '#43a047',
                                color: '#fff',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                              }}
                            />
                          )}
                          {isOnUber && hasDifferences && (
                            <>
                              <Chip
                                label="On Uber"
                                size="small"
                                sx={{
                                  bgcolor: '#43a047',
                                  color: '#fff',
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                }}
                              />
                              <Chip
                                label={`Differs: ${uberMatch!.differences!.join(', ')}`}
                                size="small"
                                sx={{
                                  bgcolor: '#ff8f00',
                                  color: '#fff',
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                }}
                              />
                            </>
                          )}
                          {!isOnUber && uberMatch !== undefined && (
                            <Chip
                              label="Not on Uber"
                              size="small"
                              variant="outlined"
                              sx={{
                                borderColor: '#9e9e9e',
                                color: '#757575',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                              }}
                            />
                          )}
                        </Box>
                        {/* Show Uber menu categories for items on Uber */}
                        {isOnUber && uberMatch?.categoryNames && uberMatch.categoryNames.length > 0 && (
                          <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mr: 0.25 }}>
                              Menus:
                            </Typography>
                            {uberMatch.categoryNames.map((name, idx) => (
                              <Chip
                                key={idx}
                                label={name}
                                size="small"
                                variant="outlined"
                                sx={{
                                  height: 22,
                                  fontSize: '0.7rem',
                                  fontWeight: 600,
                                  borderColor: '#43a047',
                                  color: '#2e7d32',
                                }}
                              />
                            ))}
                          </Box>
                        )}
                        {getProductDescription(product) && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mt: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                            }}
                          >
                            {getProductDescription(product)}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 800, color: TEAL, whiteSpace: 'nowrap' }}
                        >
                          {formatCurrency(getProductPrice(product))}
                        </Typography>
                        {isOnUber ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleSelectProduct(product)}
                              sx={{
                                borderColor: TEAL,
                                color: TEAL,
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 2,
                                fontSize: '0.8rem',
                              }}
                            >
                              Select (Add New)
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleSelectForUpdate(result)}
                              startIcon={<UpdateIcon sx={{ fontSize: '16px !important' }} />}
                              sx={{
                                bgcolor: '#ff8f00',
                                '&:hover': { bgcolor: '#e65100' },
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 2,
                                fontSize: '0.8rem',
                              }}
                            >
                              Update on Uber
                            </Button>
                          </Box>
                        ) : (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleSelectProduct(product)}
                            sx={{
                              mt: 1.5,
                              bgcolor: TEAL,
                              '&:hover': { bgcolor: TEAL_DARK },
                              textTransform: 'none',
                              fontWeight: 600,
                              px: 2.5,
                            }}
                          >
                            Select
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>
      )}

      {!searching && searchResults.length === 0 && !error && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            color: 'text.disabled',
          }}
        >
          <SearchIcon sx={{ fontSize: 56, mb: 1, opacity: 0.4 }} />
          <Typography variant="body1">
            Search for a catalog product to add to your Uber Eats menu.
          </Typography>
        </Box>
      )}
    </Box>
  );

  // ---------------------------------------------------------------------------
  // Render — Step 2: Edit & Transform
  // ---------------------------------------------------------------------------

  const renderStep2 = () => {
    if (!selectedProduct) return null;

    const priceIsValid = editPrice !== '' && editPrice > 0;
    const tier1 = getProductPrice(selectedProduct);
    const formulaPrice = tier1 > 0 ? calcUberPrice(tier1, markupDivisor) : null;

    return (
      <Box>
        {/* Product preview header */}
        <Card
          variant="outlined"
          sx={{
            mb: 3,
            borderRadius: 3,
            bgcolor: TEAL_LIGHT,
            borderColor: TEAL,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
              <ProductAvatar
                url={editImageUrl || getProductImage(selectedProduct)}
                name={editName || selectedProduct.Name}
                size={120}
              />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: '#1a1a1a' }}>
                  {selectedProduct.Name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={selectedProduct.SKU}
                    size="small"
                    variant="outlined"
                    sx={{ fontFamily: 'monospace', borderColor: TEAL, color: TEAL_DARK }}
                  />
                  {selectedProduct.Category && (
                    <Chip label={selectedProduct.Category} size="small" variant="outlined" sx={{ borderColor: TEAL, color: TEAL_DARK }} />
                  )}
                </Box>
                <Typography variant="body2" sx={{ color: '#555' }}>
                  Tier 1 price:{' '}
                  <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                    {formatCurrency(tier1)}
                  </Typography>
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Editable fields */}
        <Card variant="outlined" sx={{ mb: 3, borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2.5, color: TEAL }}>
              <EditIcon sx={{ fontSize: 18, mr: 0.75, verticalAlign: 'text-bottom' }} />
              Edit Product Details
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                label="Product Name"
                size="small"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                fullWidth
              />

              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Description
                  </Typography>
                  <Chip
                    label={descriptionMode === 'short' ? 'Short' : 'Full'}
                    size="small"
                    onClick={() =>
                      setDescriptionMode((m) => (m === 'short' ? 'full' : 'short'))
                    }
                    variant="outlined"
                    sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
                  />
                </Box>
                <TextField
                  size="small"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  fullWidth
                  multiline
                  rows={descriptionMode === 'short' ? 2 : 5}
                  placeholder="Product description for Uber Eats..."
                />
              </Box>

              <Divider />

              {/* Price — Uber formula: Tier1 / divisor */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: TEAL }}>
                Pricing
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <TextField
                  label="Uber Price (Rands)"
                  type="number"
                  size="small"
                  value={editPrice}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEditPrice(val === '' ? '' : parseFloat(val));
                  }}
                  required
                  error={editPrice === '' || editPrice === 0}
                  helperText={
                    editPrice === '' || editPrice === 0
                      ? 'Uber price is required'
                      : `= ${Math.round((editPrice as number) * 100)} cents to Uber`
                  }
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R</InputAdornment>,
                  }}
                  sx={{ flex: '1 1 160px', maxWidth: 220 }}
                />
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pt: 0.5, alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={applyUberFormula}
                    sx={{
                      textTransform: 'none',
                      bgcolor: TEAL,
                      '&:hover': { bgcolor: TEAL_DARK },
                    }}
                  >
                    Recalculate (Tier1 / {markupDivisor})
                  </Button>
                  {formulaPrice !== null && (
                    <Typography variant="body2" color="text.secondary">
                      {formatCurrency(tier1)} / {markupDivisor} = <strong>{formatCurrency(formulaPrice)}</strong>
                    </Typography>
                  )}
                </Box>
              </Box>

              <Divider />

              {/* Image URL */}
              {isUpdateMode && selectedUberMatch?.imageUrl && (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', p: 1.5, bgcolor: '#fff8e1', borderRadius: 2, border: '1px solid #ffe082' }}>
                  <Avatar
                    src={selectedUberMatch.imageUrl}
                    variant="rounded"
                    sx={{ width: 64, height: 64 }}
                  >
                    <ImageIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#e65100' }}>
                      Current Uber Image
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      This is what's currently on Uber Eats. The field below will replace it.
                    </Typography>
                  </Box>
                </Box>
              )}
              <TextField
                label="Image URL"
                size="small"
                value={editImageUrl}
                onChange={(e) => setEditImageUrl(e.target.value)}
                fullWidth
                placeholder="https://..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ImageIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                helperText={isUpdateMode ? 'Pre-filled from the catalog (source of truth). Clear to keep current Uber image.' : 'Optional. Pre-filled from the catalog if available.'}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Preview panel */}
        <Card
          variant="outlined"
          sx={{ mb: 3, borderRadius: 3, bgcolor: '#f5f5f5', borderColor: '#ccc' }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#333' }}>
              What will be sent to Uber
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
              <li>
                <Typography variant="body2" sx={{ color: '#333' }}>
                  <strong>Name:</strong> {editName || selectedProduct.Name}
                </Typography>
              </li>
              <li>
                <Typography variant="body2" sx={{ color: '#333' }}>
                  <strong>Description:</strong>{' '}
                  {editDescription
                    ? editDescription.length > 80
                      ? editDescription.substring(0, 80) + '...'
                      : editDescription
                    : '(none)'}
                </Typography>
              </li>
              <li>
                <Typography variant="body2" sx={{ color: '#333' }}>
                  <strong>Price:</strong>{' '}
                  {editPrice !== '' && editPrice > 0 ? (
                    <Typography component="span" sx={{ fontWeight: 700, color: TEAL }}>
                      {formatCurrency(editPrice as number)} ({Math.round((editPrice as number) * 100)} cents)
                    </Typography>
                  ) : (
                    <Typography component="span" color="error">
                      Not set
                    </Typography>
                  )}
                </Typography>
              </li>
              <li>
                <Typography variant="body2" sx={{ color: '#333' }}>
                  <strong>Image:</strong> {editImageUrl ? 'Custom URL' : 'None'}
                </Typography>
              </li>
              <li>
                <Typography variant="body2" sx={{ color: '#333' }}>
                  <strong>SKU:</strong> {selectedProduct.SKU}
                </Typography>
              </li>
            </Box>
          </CardContent>
        </Card>

        {/* Navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            onClick={goBack}
            startIcon={<BackIcon />}
            sx={{ borderColor: TEAL, color: TEAL }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={goNext}
            disabled={!priceIsValid}
            sx={{ bgcolor: TEAL, '&:hover': { bgcolor: TEAL_DARK }, px: 4 }}
          >
            Next: Select Categories
          </Button>
        </Box>
      </Box>
    );
  };

  // ---------------------------------------------------------------------------
  // Render — Step 3: Categories
  // ---------------------------------------------------------------------------

  const renderStep3 = () => (
    <Box>
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: TEAL }}>
              <CategoryIcon sx={{ fontSize: 18, mr: 0.75, verticalAlign: 'text-bottom' }} />
              Uber Eats Categories
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {isUpdateMode && (
                <Chip
                  label="Pre-filled from current Uber menu"
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', borderColor: '#ff8f00', color: '#e65100' }}
                />
              )}
            {selectedCategoryIds.length > 0 && (
              <Chip
                label={`${selectedCategoryIds.length} selected`}
                size="small"
                sx={{ bgcolor: TEAL, color: '#fff', fontWeight: 600 }}
              />
            )}
            </Box>
          </Box>

          {loadingCategories && (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <CircularProgress sx={{ color: TEAL, mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Loading categories from Uber Eats...
              </Typography>
            </Box>
          )}

          {!loadingCategories && categories.length > 0 && (
            <>
              <TextField
                placeholder="Filter categories..."
                size="small"
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
                fullWidth
                sx={{ mb: 2.5 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                {filteredCategories.map((cat) => {
                  const isSelected = selectedCategoryIds.includes(cat.id);
                  return (
                    <Chip
                      key={cat.id}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <span>{cat.title}</span>
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{
                              bgcolor: isSelected ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.08)',
                              borderRadius: 1,
                              px: 0.75,
                              py: 0.1,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                            }}
                          >
                            {cat.itemCount}
                          </Typography>
                        </Box>
                      }
                      variant={isSelected ? 'filled' : 'outlined'}
                      onClick={() => toggleCategory(cat.id)}
                      sx={{
                        height: 38,
                        borderRadius: 2,
                        cursor: 'pointer',
                        fontWeight: 600,
                        transition: 'all 0.15s ease',
                        ...(isSelected
                          ? {
                              bgcolor: TEAL,
                              color: '#fff',
                              '&:hover': { bgcolor: TEAL_DARK },
                              borderColor: TEAL,
                            }
                          : {
                              borderColor: '#ccc',
                              '&:hover': { borderColor: TEAL, bgcolor: TEAL_LIGHT },
                            }),
                      }}
                    />
                  );
                })}
              </Box>

              {filteredCategories.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                  No categories match your filter.
                </Typography>
              )}
            </>
          )}

          {!loadingCategories && categories.length === 0 && !error && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CategoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No categories loaded. Check your Uber token and store ID.
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={loadCategories}
                sx={{ mt: 2, borderColor: TEAL, color: TEAL }}
              >
                Retry
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={goBack}
          startIcon={<BackIcon />}
          sx={{ borderColor: TEAL, color: TEAL }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={goNext}
          disabled={selectedCategoryIds.length === 0}
          sx={{ bgcolor: TEAL, '&:hover': { bgcolor: TEAL_DARK }, px: 4 }}
        >
          {selectedCategoryIds.length === 0 ? 'Select at least 1 category' : 'Next: Review & Upload'}
        </Button>
      </Box>
    </Box>
  );

  // ---------------------------------------------------------------------------
  // Render — Step 4: Review & Upload
  // ---------------------------------------------------------------------------

  const renderStep4 = () => {
    if (!selectedProduct) return null;

    const selectedCats = categories.filter((c) => selectedCategoryIds.includes(c.id));

    return (
      <Box>
        {/* Success state */}
        {uploadResult && (
          <Card
            variant="outlined"
            sx={{
              mb: 3,
              borderRadius: 3,
              borderColor: '#43a047',
              background: 'linear-gradient(135deg, #e8f5e9 0%, #fff 100%)',
            }}
          >
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <CheckIcon sx={{ fontSize: 64, color: '#43a047', mb: 1.5 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#2e7d32' }}>
                {isUpdateMode ? 'Product Updated Successfully' : 'Product Added Successfully'}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, color: '#666' }}>
                {uploadResult.message || (isUpdateMode
                  ? 'The product has been updated on your Uber Eats menu.'
                  : 'The product has been added to your Uber Eats menu.')}
              </Typography>
              {uploadResult.itemId && (
                <Chip
                  label={`Uber ID: ${uploadResult.itemId}`}
                  variant="outlined"
                  sx={{ fontFamily: 'monospace', color: '#333', borderColor: '#aaa' }}
                />
              )}
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    setStep(0);
                    setSelectedProduct(null);
                    setSearchResults([]);
                    setSearchTerm('');
                    setUploadResult(null);
                    setError(null);
                    setIsUpdateMode(false);
                    setSelectedUberMatch(null);
                  }}
                  startIcon={<AddIcon />}
                  sx={{ bgcolor: TEAL, '&:hover': { bgcolor: TEAL_DARK }, mr: 2 }}
                >
                  Add Another Product
                </Button>
                <Button
                  variant="outlined"
                  onClick={onBack}
                  sx={{ borderColor: TEAL, color: TEAL }}
                >
                  Back to Tools
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Review summary */}
        {!uploadResult && (
          <>
            <Card variant="outlined" sx={{ mb: 3, borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2.5, color: TEAL }}>
                  Review Summary
                </Typography>

                <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
                  <ProductAvatar
                    url={editImageUrl || getProductImage(selectedProduct)}
                    name={editName || selectedProduct.Name}
                    size={100}
                  />
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {editName || selectedProduct.Name}
                    </Typography>
                    <Chip
                      label={selectedProduct.SKU}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: 'monospace', mb: 1.5 }}
                    />
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 800, color: TEAL, my: 1 }}
                    >
                      {editPrice !== '' ? formatCurrency(editPrice as number) : 'No price set'}
                    </Typography>
                    {editDescription && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {editDescription.length > 120
                          ? editDescription.substring(0, 120) + '...'
                          : editDescription}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Divider sx={{ mb: 2.5 }} />

                {/* Details grid */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '140px 1fr',
                    gap: 1.5,
                    '& .label': { fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' },
                    '& .value': { fontSize: '0.875rem' },
                  }}
                >
                  <Typography className="label">Store ID</Typography>
                  <Typography className="value" sx={{ fontFamily: 'monospace', fontSize: '0.8rem !important' }}>
                    {storeId}
                  </Typography>

                  <Typography className="label">Catalog price</Typography>
                  <Typography className="value">{formatCurrency(getProductPrice(selectedProduct))}</Typography>

                  <Typography className="label">Uber Price</Typography>
                  <Typography className="value" sx={{ fontWeight: 700, color: TEAL }}>
                    {editPrice !== '' ? formatCurrency(editPrice as number) : '-'}
                  </Typography>

                  <Typography className="label">Image</Typography>
                  <Typography className="value">
                    {editImageUrl ? (
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{
                          wordBreak: 'break-all',
                          color: 'text.secondary',
                          fontSize: '0.8rem',
                        }}
                      >
                        {editImageUrl.length > 60
                          ? editImageUrl.substring(0, 60) + '...'
                          : editImageUrl}
                      </Typography>
                    ) : (
                      <Typography component="span" variant="body2" color="text.disabled">
                        None
                      </Typography>
                    )}
                  </Typography>

                  <Typography className="label">Categories</Typography>
                  <Box>
                    {selectedCats.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {selectedCats.map((cat) => (
                          <Chip
                            key={cat.id}
                            label={cat.title}
                            size="small"
                            sx={{ bgcolor: TEAL_LIGHT, color: TEAL_DARK, fontWeight: 600 }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.disabled">
                        No categories selected
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Upload button */}
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={() => setConfirmOpen(true)}
              disabled={uploading || editPrice === '' || editPrice === 0}
              startIcon={
                uploading ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  <UploadIcon />
                )
              }
              sx={{
                bgcolor: TEAL,
                '&:hover': { bgcolor: TEAL_DARK },
                py: 1.75,
                fontSize: '1.05rem',
                fontWeight: 700,
                borderRadius: 3,
                textTransform: 'none',
                mb: 2,
              }}
            >
              {uploading
                ? (isUpdateMode ? 'Updating on Uber Menu...' : 'Adding to Uber Menu...')
                : (isUpdateMode ? 'Update on Uber Menu' : 'Add to Uber Menu')}
            </Button>

            {uploading && (
              <LinearProgress
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  '& .MuiLinearProgress-bar': { bgcolor: TEAL },
                  bgcolor: TEAL_LIGHT,
                }}
              />
            )}

            {/* Navigation */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Button
                variant="outlined"
                onClick={goBack}
                disabled={uploading}
                startIcon={<BackIcon />}
                sx={{ borderColor: TEAL, color: TEAL }}
              >
                Back
              </Button>
            </Box>
          </>
        )}

        {/* Confirmation dialog */}
        <Dialog
          open={confirmOpen}
          onClose={() => !uploading && setConfirmOpen(false)}
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>Confirm Menu Update</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              You are about to <strong>{isUpdateMode ? 'update' : 'add'}</strong>{' '}
              <strong>{editName || selectedProduct.Name}</strong> at{' '}
              <strong>{editPrice !== '' ? formatCurrency(editPrice as number) : '-'}</strong>{' '}
              {isUpdateMode ? 'on' : 'to'} the live Uber Eats menu.
            </Typography>
            {selectedCats.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Categories: {selectedCats.map((c) => c.title).join(', ')}
              </Typography>
            )}
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              This will modify the live Uber Eats menu. The product will be immediately visible to
              customers. Please double-check the details before confirming.
            </Alert>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 1 }}>
            <Button
              onClick={() => setConfirmOpen(false)}
              disabled={uploading}
              sx={{ color: 'text.secondary' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={uploading}
              startIcon={
                uploading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <UploadIcon />
                )
              }
              sx={{ bgcolor: TEAL, '&:hover': { bgcolor: TEAL_DARK } }}
            >
              {uploading ? 'Uploading...' : (isUpdateMode ? 'Confirm & Update' : 'Confirm & Add')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------

  return (
    <Box sx={{ p: 3, maxWidth: 860 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
        <IconButton onClick={onBack} disabled={uploading}>
          <BackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {isUpdateMode ? 'Update Product on Uber Eats' : 'Add Product to Uber Eats'}
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, ml: 6 }}>
        Search catalog products, customize details, choose categories, and publish to your Uber Eats
        store menu.
      </Typography>

      {/* Stepper */}
      <WizardStepper activeStep={step} />

      {/* Error banner */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ mb: 3, borderRadius: 2 }}
        >
          {error}
        </Alert>
      )}

      {/* Step content */}
      {step === 0 && renderStep1()}
      {step === 1 && renderStep2()}
      {step === 2 && renderStep3()}
      {step === 3 && renderStep4()}
    </Box>
  );
};

export default AddProductToUber;
